import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { sturgesBinCount, binValues } from '../../utils/numericBinning';

export default function TimeToCompleteDistribution({ data }) {
  const durations = useMemo(() => data.completion_durations_seconds || [], [data.completion_durations_seconds]);

  const { chartData, mean, median, stdDev, min, max } = useMemo(() => {
    if (!durations.length) return { chartData: [], mean: null, median: null, stdDev: null, min: null, max: null };

    const sorted = [...durations].sort((a, b) => a - b);
    const n = sorted.length;
    const mean = sorted.reduce((a, b) => a + b, 0) / n;
    const median = n % 2 ? sorted[Math.floor(n / 2)] : (sorted[n/2 - 1] + sorted[n/2]) / 2;
    // BUG-117: sample std-dev divides by (n-1). With n=1 that's 0/0 = NaN,
    // and the formatter below was rendering it as "NaNs" (NaN with the
    // seconds unit appended). With one sample the std dev is undefined —
    // return null so fmt() renders "—" instead.
    const stdDev = n >= 2
        ? Math.sqrt(sorted.reduce((s, v) => s + (v - mean) ** 2, 0) / (n - 1))
        : null;
    const min = sorted[0], max = sorted[n - 1];

    const binCount = sturgesBinCount(n);
    const { binnedLabels, assignedBins } = binValues(durations, binCount);
    const freq = {};
    binnedLabels.forEach(l => { freq[l] = 0; });
    assignedBins.forEach(b => { if (b) freq[b] = (freq[b] || 0) + 1; });
    const chartData = binnedLabels.map(l => ({ bin: l, count: freq[l] || 0 }));

    return { chartData, mean, median, stdDev, min, max };
  }, [durations]);

  if (!durations.length) return <div><h3>Time to Complete Distribution</h3><p>No completed sessions.</p></div>;

  const fmt = v => v != null ? `${Math.round(v)}s` : '—';

  return (
    <div>
      <h3>Time to Complete Distribution</h3>
      <div className="perf-stats-box">
        Mean: {fmt(mean)} · Median: {fmt(median)} · Std Dev: {fmt(stdDev)} · Min: {fmt(min)} · Max: {fmt(max)}
        {data.truncated && <span style={{marginLeft: 8, color: '#f59e0b'}}>(first 5000 sessions shown)</span>}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 10, right: 30, bottom: 60, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bin" angle={-45} textAnchor="end" interval={0} />
          <YAxis />
          <Tooltip />
          <Bar dataKey="count" fill="#3b82f6" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
